<!-- eslint-disable vue/no-v-html -->
<template>
  <ContainerBasic container-type="CONTENT">
    <div
      class="flex flex-col self-center justify-center touch:md:flex-row lg:flex-row"
    >
      <div class="flex flex-col items-center">
        <img
          src="@/assets/images/TEMP-calendar-day.svg"
          class="mb-14 touch:md:mb-0 lg:mb-0 h-[150px] w-[150px] touch:md:h-[210px] touch:md:w-[210px] lg:h-[210px] lg:w-[210px]"
          loading="lazy"
        />
      </div>
      <ul
        v-if="blockData.events"
        class="mx-auto touch:md:ml-44 lg:ml-44 xs-w[100%] md:w-[576px]"
      >
        <li
          v-for="(event, index) in blockData.events"
          :key="index"
          class="flex flex-col"
        >
          <div
            class="flex justify-between px-md touch:md:justify-between lg:justify-between"
          >
            <div class="flex flex-col w-full text-base text-md">
              <div class="flex flex-row flex-wrap items-baseline">
                <FaIcon
                  classes="mr-xs fa-fw shrink-0"
                  icon-class="fal fa-calendar-days"
                />
                <div
                  class="flex flex-row flex-wrap items-baseline shrink-0 max-w-[calc(100%-50px)]"
                >
                  <div
                    class="w-auto"
                    v-html="getStyledDateAsHtml(event.date)"
                  ></div>
                  <div
                    class="w-full md:w-auto"
                    v-html="getAdditionalDates(event)"
                  ></div>
                </div>
                <div
                  v-if="event.date && event.additionalDates.length === 0"
                  class="w-full ml-0 md:w-auto md:ml-sm"
                >
                  <FaIcon
                    icon-class="fal fa-clock"
                    classes="mr-xs fa-fw touch:md:ml-sm lg:ml-sm"
                  />
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <span v-html="getTime(event)"></span>
                </div>
              </div>

              <div class="mt-1 text-base leading-7">
                <div class="flex flex-col">
                  <div
                    class="flex flex-row flex-wrap items-baseline content-center md:flex-nowrap"
                  >
                    <FaIcon
                      icon-class="fal fa-location-dot"
                      classes="mr-xs fa-fw shrink-0"
                    />
                    <span
                      v-if="event.location.street != undefined"
                      :class="
                        event.location.street.length > 20
                          ? 'max-w-[9rem] md:max-w-full'
                          : 'whitespace-nowrap'
                      "
                    >
                      {{ event.location.street }}&nbsp;{{
                        event.location.streetNumber
                      }}
                    </span>
                    <span v-else>
                      {{ event.location.zipCode ?? '' }}
                      {{ event.location.title }}
                    </span>
                    <span
                      v-if="event.location.street != undefined"
                      class="hidden ml-xs mr-xs touch:md:inline-flex lg:inline-flex"
                      >|</span
                    >
                    <span
                      v-if="event.location.street != undefined"
                      class="ml-md touch:md:ml-0 lg:ml-0"
                      >{{ event.location.zipCode ?? '' }}
                      {{ event.location.title }}</span
                    >
                  </div>
                  <div
                    v-if="event.courseType === ECourseType.HYBRID"
                    class="flex flex-row w-full"
                  >
                    <span class="ml-md">
                      {{ `${t('event.onlineLocationAddition')}&nbsp;` }}
                    </span>
                  </div>
                </div>
              </div>
              <LinkBasic
                :label="t('event.showDetails')"
                :link="event.slug"
                target="_blank"
                icon="fal fa-chevron-right"
                class="mt-md touch:md:mt-sm lg:mt-sm text-vdv-base min-w-[12rem] md:min-w-[9rem]"
              />
            </div>
            <EventCourseIndicator
              class="!ml-xs touch:md:!ml-md lg:!ml-md"
              :course-type="event.courseType as ECourseType"
              :is-clickable="false"
            />
          </div>
          <hr
            v-if="index != Object.keys(blockData.events).length - 1"
            class="w-full border-t-1 my-md border-t-vdv-border-light"
          />
        </li>
      </ul>
    </div>
  </ContainerBasic>
</template>

<script setup lang="ts">
import dayjs, {
  getWeekdayLocale,
  getMonthLocale,
  getHoursLocale,
} from '@/helpers/dayJs';
import ContainerBasic from '@/components/components/container-basic.vue';
import { ECourseType } from '@/@types/course-type';
import LinkBasic from '@/components/components/core/basicLink/basic-link.vue';
import EventCourseIndicator from '@/components/event/event-course-indicator.vue';
import type { EventEnumerationData } from './blockType';
import type { EventData } from '~~/src/server/transformers/EntityTransformers/eventTransformer';
import FaIcon from '@/components/fa-icon.vue';

const { t } = useTrans();

defineProps({
  blockData: {
    type: Object as PropType<EventEnumerationData>,
    required: true,
    default: {} as EventEnumerationData,
  },
});
// Language should be a global var (in a store) at some point

function getStyledDateAsHtml(date: Date) {
  if (!date) {
    return `<span>${t('event.individualAppointment')}</span>`;
  }
  const language = 'de';
  const day = dayjs(date).format('DD');
  const dayWord = getWeekdayLocale(date, language).slice(0, 2);
  const month = getMonthLocale(date, language);
  const year = dayjs(date).format('YYYY');
  return `${dayWord}. <span class="text-lg"><b> ${day}. </b></span> ${month} ${year}`;
}

function getTime(event: EventData) {
  const language = 'de';
  const startTime = getHoursLocale(event.startTime, language);
  const endTime = getHoursLocale(event.endTime, language);
  return `${startTime} - ${endTime}`;
}

function getAdditionalDates(event: EventData) {
  if (event.additionalDates?.length === 0) return '';
  if (!event.hasConsecutiveDates) {
    if (event.additionalDates.length === 1) {
      return `<div>&nbsp;& ${t('event.additionalDate')}</div>`;
    } else {
      return `<div>&nbsp;+ ${t('event.additionalDates', {
        count: event.additionalDates.length,
      })}</div>`;
    }
  } else {
    return `<span class="hidden px-xs md:inline">-</span>${getStyledDateAsHtml(
      event.additionalDates[event.additionalDates.length - 1].date,
    )}`;
  }
}
</script>
